import { Chart, useChart } from '@chakra-ui/charts';
import { Center, Text, useToken, VStack } from '@chakra-ui/react';
import ChartWrapper from '@components/ui/chart/ChartWrapper';
import { formatUnit, OtherUnits } from '@utils/unit.utils';
import { FormattedMessage, useIntl } from 'react-intl';
import { Label, Pie, PieChart, PieLabelRenderProps, Sector, Tooltip, TooltipContentProps } from 'recharts';

import { DRONE_STATS_NO_PLATFORM_CODE, PlatformDroneCountType } from '@/types/data/statistic.types';

type Props = {
  data: PlatformDroneCountType[];
  dronesCount: number;
  getPlatformColor: (value: string) => string;
};

const CustomPieTooltip = ({ active, payload, total }: TooltipContentProps & { total: number }) => {
  const isVisible = active && payload.length !== 0;
  return (
    <Center paddingY={1} paddingX={1.5} backgroundColor="neutral.black">
      {isVisible && (
        <Text>{`${payload[0].name} - ${Number(payload[0].value)} (${formatUnit(OtherUnits.PERCENT, (Number(payload[0].value) / total) * 100, { precision: 2 })})`}</Text>
      )}
    </Center>
  );
};

const CustomizedLabel = ({ x, y, value, total }: PieLabelRenderProps & { total: number }) => {
  const [neutral500] = useToken('colors', ['neutral.500']);

  const [fontStandard] = useToken('fontSizes', ['sm']);

  if (value === 0 || value / total < 0.03) {
    return null;
  }
  return (
    <text x={x} y={y} fill={neutral500} style={{ fontSize: fontStandard }} textAnchor="middle">
      {value}
    </text>
  );
};

const CustomLabel = ({ total }: Readonly<{ total: number }>) => {
  return (
    <foreignObject width="100%" height="100%">
      <Center width="100%" height="100%">
        <VStack gap={0.25}>
          <Text fontSize={24} fontWeight="medium">
            {total}
          </Text>
          <Text color="neutral.300" whiteSpace="pre-line" textAlign="center">
            <FormattedMessage id="components.notificationSearch.reporting.totalDetected" />
          </Text>
        </VStack>
      </Center>
    </foreignObject>
  );
};

export default function DroneByPlatformsPie({ data, dronesCount, getPlatformColor }: Readonly<Props>) {
  const { formatMessage } = useIntl();

  const chart = useChart({
    data: data
      .toSorted((a, b) => b.count - a.count)
      .map((entry) => ({
        name:
          entry.platform === DRONE_STATS_NO_PLATFORM_CODE
            ? formatMessage({ id: 'components.notificationSearch.reporting.noPlatform' })
            : entry.platformName,
        value: entry.count,
        color: getPlatformColor(entry.platform),
      })),
  });

  return (
    <ChartWrapper
      width="410px"
      isEmpty={chart.getTotal('value') === 0}
      emptyLabel={formatMessage({ id: 'components.notificationSearch.reporting.noDetection' })}
      title={formatMessage({ id: 'components.notificationSearch.reporting.nbDronesByPlatforms' })}
    >
      <Chart.Root chart={chart}>
        <PieChart width="100%" height={348}>
          <Tooltip
            cursor={false}
            isAnimationActive={false}
            content={(props) => <CustomPieTooltip total={dronesCount} {...props} />}
          />
          <Pie
            innerRadius={102}
            outerRadius={124}
            startAngle={90}
            endAngle={-270}
            label={(props) => <CustomizedLabel total={dronesCount} {...props} />}
            data={chart.data}
            nameKey="name"
            strokeWidth={0}
            shape={(props) => <Sector {...props} fill={chart.color(props.payload.color)} />}
          />
          <Label position="center" content={() => <CustomLabel total={dronesCount} />} />
        </PieChart>
      </Chart.Root>
    </ChartWrapper>
  );
}
